import type { AlertSeverity, RiskTimelineEvent, SignalId, SignalState, UserSignalStatus } from '@core/api/complianceTypes';
import { PHASE2_SIGNALS, SCORE_EXEMPT_SIGNALS } from '@core/api/complianceRules';

/**
 * The S1 to S21 catalog of the compliance plan (section 2.2), as copy: the name, the starting threshold
 * (D6 dev values), the severity and, for the signals the phase-2 evaluator does not light, the reason
 * (plan 2.3). The backend sends the numbers; this file says what they mean. No entry reads text.
 *
 * **Wording (compliance review, 2026-09-27).** A name says what the RECORD shows, never what the trader
 * meant: S2 and S3 count the checker's flags on the AI's replies, so they read "replies the checker flagged",
 * not "advice given" or "attempts". A name here is what a regulator or opposing counsel would read next to a
 * customer's email, so it must not state as fact something only a checker inferred.
 */
export interface SignalMeta {
  id: SignalId;
  /**
   * What the screen shows (display plan section 3): the plain words that lead. They say what the record shows,
   * never what the trader meant (compliance review, 2026-09-27).
   */
  plain: string;
  /** The catalog's name (plan 2.2), kept for the hover beside the S-id. */
  name: string;
  severity: AlertSeverity;
  threshold: string;
  /**
   * What the reviewer does when it lights (plan 2.2), one sentence. Where it needs the text, it names the monthly
   * audit review: the console cannot show a request or a reply until the per-request review (phase 3) exists.
   */
  action: string;
  /** Present on the signals phase 2 does not evaluate: why, and what closes it. */
  notRecordedReason?: string;
  /** Present on the signals that never add points (`SCORE_EXEMPT_SIGNALS`): why, in a few words. */
  scoreExempt?: string;
}

const FIRM_CONTROL = 'a firm control, not the trader';

export const SIGNAL_CATALOG: readonly SignalMeta[] = [
  { id: 'S1',  plain: 'Answers held back repeatedly', name: 'Repeated blocks for one user', severity: 'p2', threshold: '2+ final or 3+ first-attempt blocks in 7 days; 5+ first-attempt blocks in 7 days: P1', action: 'Read each held-back request in the monthly audit review; one disposition per request.' },
  { id: 'S2',  plain: 'Replies the checker flagged as advice or a forecast', name: 'Advice and prediction hits', severity: 'p2', threshold: '3+ advice-prescription or market-prediction blocks in 30 days', action: 'Read each flagged reply in the monthly audit review; record what the reply said and whether the trader saw it, not what the trader is like.' },
  { id: 'S3',  plain: 'Replies the checker flagged for breaking the assistant\'s rules', name: 'Rule-break hits on replies', severity: 'p2', threshold: '1 hit: P2; 2+ in 30 days: P1', action: 'Read the request and the reply in the monthly audit review. These are checks on the reply: whether the trader tried to get round the rules is a person\'s call from the text, never from this count.' },
  { id: 'S4',  plain: 'The model wrote lines as the trader', name: 'Invented trader turns', severity: 'p3', threshold: '2+ role-label-leak in 30 days', action: 'Read the cut text in the monthly audit review; check nothing was finalised from it.', notRecordedReason: 'Evaluator not built in phase 2; the rule hits are on the overview.' },
  { id: 'S5',  plain: 'A day total that did not match the facts', name: 'Wrong day totals', severity: 'p2', threshold: 'any contradicted-day-total (P1 if shown)', action: 'Check the day facts against the reply; a shown wrong number is an escalation.', notRecordedReason: 'Evaluator not built in phase 2; the rule hits are on the overview.' },
  { id: 'S6',  plain: 'Numbers the model was not given', name: 'Ungrounded numbers and unlabelled prices', severity: 'p3', threshold: 'trend only; 5+ in 30 days joins the sample', action: 'Include the user in the monthly sample.', notRecordedReason: 'Evaluator not built in phase 2; the rule hits are on the overview.' },
  { id: 'S7',  plain: 'Heavy use of Consult chat (no checker there)', name: 'The unchecked surface used heavily', severity: 'p2', threshold: '20+ Consult turns in a day or 100+ in 30 days', action: 'Sample 5 turns in the monthly audit review.', notRecordedReason: 'Evaluator not built in phase 2.' },
  { id: 'S8',  plain: 'AI used without accepting the current disclaimer', name: 'AI use with no current acceptance', severity: 'p1', threshold: 'any gated output with no acceptance of the current version', action: 'Verify the row; escalate as a control gap and file a bug. No customer action.', scoreExempt: FIRM_CONTROL },
  { id: 'S9',  plain: 'AI activity where it should be off', name: 'A row on a dark surface, or AI in prod', severity: 'p1', threshold: 'any', action: 'Same-day escalation to the owner; record the ids.', scoreExempt: FIRM_CONTROL },
  { id: 'S10', plain: 'An outside account using dev AI', name: 'Real people on dev AI', severity: 'p2', threshold: 'any AI row from an email domain outside the firm and test list', action: 'Confirm who the account is; a real customer is the owner\'s decision.' },
  { id: 'S11', plain: 'Unusual spend or credits', name: 'Spend and credit anomalies', severity: 'p3', threshold: 'cost above 3x the median; 10+ absorbed calls in a day (P2); 3+ admin grants in 30 days', action: 'Check for scripted use; an absorbed-call burst joins S1\'s review.' },
  { id: 'S12', plain: 'Repeated data exports', name: 'Data export requests', severity: 'p3', threshold: '2+ exports in 7 days (5+: P2)', action: 'Context only: a data request is the trader\'s right. With S1 to S3 lit, tell the owner; litigation-hold readiness is the owner\'s and counsel\'s call.', notRecordedReason: 'Needs v2_data_export_requests (D10, phase 5); today only a log marker exists.', scoreExempt: 'a data right, never scored' },
  { id: 'S13', plain: 'Strategy code taken away', name: 'Strategy code exports', severity: 'p3', threshold: 'trend only; 10+ in 30 days for the sample', action: 'Include the translation in the sample.', notRecordedReason: 'Needs v2_ai_code_exports (D10, phase 5); nothing is recorded today.' },
  { id: 'S14', plain: 'Translations the model refused', name: 'Refused translations', severity: 'p2', threshold: '2+ refusals in 30 days', action: 'Read the refused translation in the monthly audit review.', notRecordedReason: 'Needs parse_status on the outputs view (D11b, phase 5).' },
  { id: 'S15', plain: 'Recaps held back, or a flagged recap emailed', name: 'Withheld recaps and flagged emails', severity: 'p3', threshold: 'any withheld recap; an emailed flagged recap is P2', action: 'Read the blocked recap in the monthly audit review; an emailed flagged recap is a "customer saw it" disposition.' },
  { id: 'S16', plain: 'Answers that used the economic calendar', name: 'Calendar-in-AI turns', severity: 'p3', threshold: 'trend only; every such turn is a sample candidate', action: 'Draw the monthly sample from these turns first.', notRecordedReason: 'Needs calendar_included on the turn (D11, phase 5); today only a log marker exists.' },
  { id: 'S17', plain: 'Several signals on the same trader', name: 'Composite: the same user across surfaces', severity: 'p1', threshold: 'any two of S1 to S3 and S14 in 30 days', action: 'The full per-user review in the monthly audit review; a note for the owner.' },
  { id: 'S18', plain: 'The model kept working out levels (never shown)', name: 'Reasoning redactions on AI Assist', severity: 'p3', threshold: 'top 5% of users with 5+ turns', action: 'Context for S1 and S2; not an incident on its own.', notRecordedReason: 'Evaluator not built in phase 2.' },
  { id: 'S19', plain: 'Many stops and restarts', name: 'Restart churn', severity: 'p3', threshold: '5+ stops or 3+ resets in a day', action: 'Context for S1.', notRecordedReason: 'Evaluator not built in phase 2; stops and resets are on the timeline.' },
  { id: 'S20', plain: 'Records the firm cannot fully explain', name: 'Provenance gaps on new rows', severity: 'p1', threshold: 'any', action: 'Escalation and a bug; not a user matter.', scoreExempt: FIRM_CONTROL },
  { id: 'S21', plain: 'Claims about other days', name: 'Cross-day claims', severity: 'p3', threshold: 'none', action: 'A known gap; the reviewer looks for it in the sample.', notRecordedReason: 'No dedicated rule; surfaces as ungrounded-fact or in the human read.' },
];

export const SIGNAL_BY_ID: ReadonlyMap<SignalId, SignalMeta> = new Map(SIGNAL_CATALOG.map(s => [s.id, s]));

export interface SignalView extends SignalMeta {
  state: SignalState;
  count: number;
  points: number;
  firstAt: string | null;
  lastAt: string | null;
  /** What the evaluator counted (the backend's `facts`); empty when nothing was counted. */
  facts: Record<string, number>;
  /** True when this signal is one the phase-2 evaluator lights. */
  evaluated: boolean;
}

/**
 * Merge the backend's per-user signal statuses over the catalog so the screen always lists all 21:
 * a signal the backend did not send is `not-recorded` (its catalog reason says why), even when it is a
 * phase-2 signal (an older backend), so the screen never claims "clear" for something nobody checked.
 */
export function mergeSignals(statuses: readonly UserSignalStatus[]): SignalView[] {
  const byId = new Map(statuses.map(s => [s.id, s]));
  return SIGNAL_CATALOG.map(meta => {
    const s = byId.get(meta.id);
    return {
      ...meta,
      severity: s?.severity ?? meta.severity,
      state: s?.state ?? 'not-recorded',
      count: s?.count ?? 0,
      points: s?.points ?? 0,
      firstAt: s?.firstAt ?? null,
      lastAt: s?.lastAt ?? null,
      facts: s?.facts ?? {},
      evaluated: PHASE2_SIGNALS.includes(meta.id),
    };
  });
}

/**
 * The one caveat every signal carries (plan section 5, "what this plan never claims"), shown wherever the
 * signals are: the counts come from the checker's flags on the AI's replies.
 */
export const CHECKER_CAVEAT =
  'These counts are the checker\'s flags on the AI\'s replies: they say what the AI wrote, not what the trader meant, and a clean paraphrase passes the checker.';

/** The score rule's exemptions in words, for the ranked list's header. `signals.test.ts` pins it to `SCORE_EXEMPT_SIGNALS`. */
export const SCORE_EXEMPT_NOTE = 'S8, S9 and S20 (the firm\'s own controls) and S12 (a data right) add no points.';

/** Whether a signal never adds points (`SCORE_EXEMPT_SIGNALS`, the backend's `RiskSignalCatalog.ScoreExempt`). */
export function isScoreExempt(id: SignalId): boolean {
  return SCORE_EXEMPT_SIGNALS.includes(id);
}

/** Of a signal's flagged replies (or recaps): how many the trader saw, how many were held back, how many are not known. */
export interface SeenByTrader {
  shown: number;
  heldBack: number;
  unknown: number;
  /** The timeline is capped (newest 200 events): fewer events than the signal's count means a partial split. */
  partial: boolean;
}

/**
 * The fact that decides a monthly-audit escalation (A14: "an output a customer SAW"), for the signals where it
 * applies, from the timeline the per-user view already has:
 *
 * - **S2, S3** (`flag` events): a `shadow` checker records and never holds back, so the reply was shown; an
 *   `enforce` checker holds back a Block hit (severity 1) and shows a Flag hit (severity 0); anything else
 *   (no mode recorded, no severity) is not known.
 * - **S15**: a `flagged-recap-emailed` event went out to the trader; a `withheld-recap` was held back.
 *
 * Null for every other signal (S1 counts withheld first answers by definition; the rest are not about a reply).
 */
export function seenByTrader(id: SignalId, count: number, events: readonly RiskTimelineEvent[]): SeenByTrader | null {
  let shown = 0;
  let heldBack = 0;
  let unknown = 0;
  if (id === 'S2' || id === 'S3') {
    for (const e of events) {
      if (e.signalId !== id || e.type !== 'flag') continue;
      if (e.checkerMode === 'shadow') shown++;
      else if (e.checkerMode === 'enforce' && e.ruleSeverity === 1) heldBack++;
      else if (e.checkerMode === 'enforce' && e.ruleSeverity === 0) shown++;
      else unknown++;
    }
  } else if (id === 'S15') {
    for (const e of events) {
      if (e.signalId !== 'S15') continue;
      if (e.type === 'flagged-recap-emailed') shown++;
      else if (e.type === 'withheld-recap') heldBack++;
    }
  } else {
    return null;
  }
  return { shown, heldBack, unknown, partial: shown + heldBack + unknown < count };
}
